import { fetchBacklogConnection } from '@/services/backlog'

/**
 * The reader's numeric Backlog account id, for the STT proxy's `config` frame.
 *
 * Backlog gives an account two identifiers and only this one is usable: `id` is
 * the number every write takes as `assignee_id`, while the field Backlog calls
 * `userId` is an opaque string on a Nulab-account space — `*we2tTkaF0L` rather
 * than the handle it looks like it should be. The wire field is named
 * `userIdBacklog` because that is what was agreed upstream; the value in it is
 * the number.
 *
 * Cached because the id cannot change for a signed-in reader: it belongs to the
 * Backlog account behind their grant, and a different account means a different
 * grant. One answer per page load is therefore not a staleness risk.
 */
let cachedUserId: number | null = null

/** Shared so two recordings starting at once make one request, not two. */
let inFlight: Promise<number | null> | null = null

/**
 * The reader's Backlog account id, fetched once and remembered.
 *
 * Never rejects. A reader with no Backlog grant is the ordinary case, not a
 * failure, and a recording must start for them exactly as it did before this
 * field existed — so every way of not having an id answers the same `null`, and
 * the caller leaves the field out.
 *
 * A `null` is deliberately not remembered: it is the one answer that can change
 * without a reload, because connecting Backlog happens mid-session and the next
 * recording should carry the id.
 *
 * @returns The numeric id, or null when there is no usable grant.
 */
export async function ensureBacklogUserId(): Promise<number | null> {
  if (cachedUserId !== null) return cachedUserId
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const connection = await fetchBacklogConnection()
      // `account` is only populated for a grant that answered just now. A
      // stored-but-rejected one reports a status and no account, which is the
      // same thing here as having none.
      //
      // Assigned either way, and a null is still not remembered: the read at
      // the top of this function only short-circuits on a number, so the next
      // caller asks again.
      cachedUserId = connection.account?.id ?? null
      return cachedUserId
    } catch {
      // Deliberately swallowed. This runs alongside the STT token fetch on the
      // path that starts a recording, and Backlog being unreachable is not a
      // reason to refuse to record a meeting.
      return null
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/**
 * Forget the cached id.
 *
 * Called when signing out from the user menu, which routes to the login screen
 * rather than reloading the document — so this module's state outlives it, and
 * the next person to sign in on the tab would otherwise have their meeting
 * stamped with the previous one's Backlog account. The session-expiry path
 * needs no such call: it goes through `location.replace`, which takes the whole
 * module with it.
 */
export function resetBacklogUserId(): void {
  cachedUserId = null
  inFlight = null
}
